/**
 * useDebugHistory - The debug records, merged from storage and from the service
 *
 * DebugService only holds the calls captured since the app started, while
 * storage holds everything from earlier sessions. The panel needs both, and it
 * has to keep re-merging: records keep arriving as calls end while the panel
 * is open, and there is no event to subscribe to.
 *
 * The polling lives in one module-level subscription rather than in each
 * caller. Both the tab bar's record count and the Debug tab itself read this
 * hook, and two independent pollers would mean two storage reads and two
 * writes every interval — writing the same records back over each other.
 */

import type { UseDebugHistoryReturn } from 'Modules/softphone/components/SoftphoneDevTools/hooks/useDebugHistory/index.types';
import {
  clearDebugHistory,
  loadDebugHistory,
  saveDebugHistory,
} from 'Modules/softphone/components/SoftphoneDevTools/storage';
import { mergeDebugHistory } from 'Modules/softphone/components/SoftphoneDevTools/utils';
import {
  DebugService,
  type CallDebugInfo,
} from 'Modules/softphone/services/DebugService';
import { useCallback, useEffect, useSyncExternalStore } from 'react';

export const DEBUG_HISTORY_REFRESH_INTERVAL_MS = 2000;

interface HistorySnapshot {
  debugHistory: CallDebugInfo[];
  isLoading: boolean;
}

let snapshot: HistorySnapshot = { debugHistory: [], isLoading: true };
const listeners = new Set<() => void>();
let subscriberCount = 0;
let pollTimer: ReturnType<typeof setInterval> | null = null;

const publish = (next: HistorySnapshot): void => {
  snapshot = next;
  listeners.forEach((listener) => listener());
};

const refresh = async (): Promise<void> => {
  const persisted = await loadDebugHistory();
  const merged = mergeDebugHistory(persisted, DebugService.getInstance().getAll());

  publish({ debugHistory: merged, isLoading: false });

  if (merged.length > 0) {
    await saveDebugHistory(merged);
  }
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  subscriberCount += 1;

  if (subscriberCount === 1) {
    void refresh();
    pollTimer = setInterval(
      () => void refresh(),
      DEBUG_HISTORY_REFRESH_INTERVAL_MS
    );
  }

  return () => {
    listeners.delete(listener);
    subscriberCount -= 1;

    if (subscriberCount === 0 && pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  };
};

const getSnapshot = (): HistorySnapshot => snapshot;

export const useDebugHistory = (): UseDebugHistoryReturn => {
  const { debugHistory, isLoading } = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getSnapshot
  );

  const clearHistory = useCallback(async () => {
    // Clear both in-memory and persisted
    DebugService.getInstance().clear();
    await clearDebugHistory();
    publish({ debugHistory: [], isLoading: false });
  }, []);

  // A record captured while the panel was closed must not wait a full interval
  // to appear when it reopens.
  useEffect(() => {
    void refresh();
  }, []);

  return { debugHistory, isLoading, clearHistory };
};
